/**
 * Parses a D&D Beyond character sheet PDF (fillable form) into a
 * structure matching the backend ImportedSheet shape.
 *
 * DDB PDFs are fillable AcroForms — all character data lives in named
 * form field annotations, not in the text stream.  We use pdfjs-dist's
 * getAnnotations() to read them, so no server-side tooling is needed.
 */

import * as pdfjsLib from 'pdfjs-dist';

// Point at the bundled worker.
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.mjs',
  import.meta.url,
).toString();

// ────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────

import type { SpellEntry } from './character';

/** Mirrors the backend ImportedSheet. */
export interface ParsedSheet {
  className: string;
  subclass?: string;
  level: number;
  hitDieTotal: number;
  baseStats: Record<string, number>;
  asiDeltas: Record<string, number>;
  featDeltas: Record<string, number>;
  proficiencies: Record<string, string>;
  feats: string[];
  spells: SpellEntry[];

  // ── Character-level metadata ──
  race?: string;
  background?: string;
  alignment?: string;
  walkSpeed?: number;
  languages?: string[];
  personalityTraits?: string;
  ideals?: string;
  bonds?: string;
  flaws?: string;

  // ── Import-only metadata (informational) ──
  armorClass?: number;
  spellcastingAbility?: string;
  spellSaveDc?: number;
  spellAttackBonus?: number;

  warnings: string[];
}

// ────────────────────────────────────────────────────────────────────
// Public API
// ────────────────────────────────────────────────────────────────────

/**
 * Parse a DDB character sheet PDF (as an ArrayBuffer from a file input)
 * into a ParsedSheet suitable for the "parsed" platform import endpoint.
 */
export async function parseDdbPdf(buffer: ArrayBuffer): Promise<ParsedSheet> {
  const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;

  // Collect all form field annotations across all pages into a flat map.
  const fields: Record<string, string> = {};
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const annotations = await page.getAnnotations();
    for (const a of annotations) {
      if (a.fieldName && a.fieldValue != null && a.fieldValue !== '') {
        fields[a.fieldName.trim()] = String(a.fieldValue);
      }
    }
  }

  const warnings: string[] = [];

  // ── Class & Level ──
  const classLevelRaw = fields['CLASS  LEVEL'] ?? fields['CLASS LEVEL'] ?? '';
  const classLevel = parseClassLevel(classLevelRaw);
  if (!classLevel) {
    throw new Error(
      `Could not parse class & level from "${classLevelRaw}". Is this a D&D Beyond character sheet?`,
    );
  }

  // ── Ability scores ──
  const baseStats: Record<string, number> = {};
  for (const key of ['STR', 'DEX', 'CON', 'INT', 'WIS', 'CHA'] as const) {
    const raw = fields[key];
    if (raw) {
      const val = parseInt(raw, 10);
      if (!isNaN(val) && val >= 1 && val <= 30) baseStats[key] = val;
    }
  }
  if (Object.keys(baseStats).length < 6) {
    warnings.push(
      `Only found ${Object.keys(baseStats).length}/6 ability scores — check base stats after import`,
    );
  }

  // ── Max HP + Hit Dice ──
  const maxHp = parseInt(fields['MaxHP'] ?? '', 10) || null;
  const hitDice = parseHitDice(fields['Total'] ?? '');
  const conMod = Math.floor(((baseStats.CON ?? 10) - 10) / 2);

  let hitDieTotal: number;
  if (maxHp != null) {
    // hitDieTotal = maxHp - level * conMod (the backend formula)
    hitDieTotal = maxHp - classLevel.level * conMod;
  } else if (hitDice) {
    hitDieTotal = hitDice.count * hitDice.die; // rough estimate
    warnings.push('MaxHP field missing — hitDieTotal is estimated from hit dice');
  } else {
    const classDie = CLASS_HIT_DICE[classLevel.className.toLowerCase()] ?? 8;
    hitDieTotal = classDie * classLevel.level;
    warnings.push('MaxHP and hit dice fields missing — hitDieTotal is a rough estimate');
  }

  // Clamp to valid range.
  const classDie = hitDice?.die ?? CLASS_HIT_DICE[classLevel.className.toLowerCase()] ?? 8;
  hitDieTotal = Math.max(classLevel.level, Math.min(classLevel.level * classDie, hitDieTotal));

  // ── Saving throw proficiencies ──
  const saveProfKeys: [string, string][] = [
    ['StrProf', 'strength'],
    ['DexProf', 'dexterity'],
    ['ConProf', 'constitution'],
    ['IntProf', 'intelligence'],
    ['WisProf', 'wisdom'],
    ['ChaProf', 'charisma'],
  ];
  const proficiencies: Record<string, string> = {};
  for (const [fieldName, ability] of saveProfKeys) {
    if (fields[fieldName] === '•' || fields[fieldName] === 'P') {
      proficiencies[`saving-throw-${ability}`] = 'proficiency';
    }
  }

  // ── Skill proficiencies ──
  const skillProfKeys: [string, string][] = [
    ['AcrobaticsProf', 'acrobatics'],
    ['AnimalProf', 'animal-handling'],
    ['ArcanaProf', 'arcana'],
    ['AthleticsProf', 'athletics'],
    ['DeceptionProf', 'deception'],
    ['HistoryProf', 'history'],
    ['InsightProf', 'insight'],
    ['IntimidationProf', 'intimidation'],
    ['InvestigationProf', 'investigation'],
    ['MedicineProf', 'medicine'],
    ['NatureProf', 'nature'],
    ['PerceptionProf', 'perception'],
    ['PerformanceProf', 'performance'],
    ['PersuasionProf', 'persuasion'],
    ['ReligionProf', 'religion'],
    ['SleightofHandProf', 'sleight-of-hand'],
    ['StealthProf', 'stealth'],
    ['SurvivalProf', 'survival'],
  ];
  for (const [fieldName, skill] of skillProfKeys) {
    if (fields[fieldName] === 'P' || fields[fieldName] === '•') {
      proficiencies[`skill-${skill}`] = 'proficiency';
    }
  }

  // ── Feats (from FeaturesTraits fields) ──
  const feats = parseFeats(fields);

  // ── Subclass (from FeaturesTraits fields) ──
  const subclass = parseSubclass(fields);

  // ── Spells ──
  const spells = parseSpells(fields, warnings);

  // ── Race ──
  const race = fields['Race'] ?? fields['RACE'] ?? undefined;

  // ── Background ──
  const background = fields['Background'] ?? fields['BACKGROUND'] ?? undefined;

  // ── Alignment ──
  const alignment = fields['Alignment'] ?? fields['ALIGNMENT'] ?? undefined;

  // ── Armor Class ──
  const acRaw = fields['AC'] ?? fields['ArmorClass'] ?? '';
  const armorClass = parseInt(acRaw, 10) || undefined;

  // ── Walk Speed ──
  const speedRaw = fields['Speed'] ?? fields['SPEED'] ?? '';
  const walkSpeed = parseInt(speedRaw, 10) || undefined;

  // ── Languages & Other Proficiencies ──
  // DDB PDFs put languages/tool/weapon/armor proficiencies in "ProficienciesLang".
  const languages = parseLanguages(fields['ProficienciesLang'] ?? '');

  // ── Currency ──
  // DDB PDFs have individual CP, SP, EP, GP, PP fields.
  // We don't persist these to the character model (wallet is on the party doc)
  // but we log them so the DM can manually enter them.
  const cp = parseInt(fields['CP'] ?? '', 10) || 0;
  const sp = parseInt(fields['SP'] ?? '', 10) || 0;
  const ep = parseInt(fields['EP'] ?? '', 10) || 0;
  const gp = parseInt(fields['GP'] ?? '', 10) || 0;
  const pp = parseInt(fields['PP'] ?? '', 10) || 0;
  if (cp + sp + ep + gp + pp > 0) {
    warnings.push(
      `Currency found: ${cp} CP, ${sp} SP, ${ep} EP, ${gp} GP, ${pp} PP — add to party wallet manually`,
    );
  }

  // ── Personality / Ideals / Bonds / Flaws ──
  const personalityTraits = fields['PersonalityTraits'] ?? fields['Personality Traits'] ?? undefined;
  const ideals = fields['Ideals'] ?? undefined;
  const bonds = fields['Bonds'] ?? undefined;
  const flaws = fields['Flaws'] ?? undefined;

  // ── Spellcasting metadata ──
  const spellcastingAbility = fields['SpellcastingAbility'] ?? fields['Spellcasting Ability'] ?? undefined;
  const spellSaveDcRaw = fields['SpellSaveDC'] ?? fields['Spell Save DC'] ?? '';
  const spellSaveDc = parseInt(spellSaveDcRaw, 10) || undefined;
  const spellAtkRaw = fields['SpellAtkBonus'] ?? fields['Spell Atk Bonus'] ?? fields['SAB'] ?? '';
  const spellAttackBonus = parseInt(spellAtkRaw, 10) || undefined;

  // ── Expertise detection ──
  // DDB PDFs mark expertise with 'E' instead of 'P' or '•' on skill prof fields.
  // Re-scan skill fields for expertise markers.
  for (const [fieldName, skill] of skillProfKeys) {
    if (fields[fieldName] === 'E') {
      proficiencies[`skill-${skill}`] = 'expertise';
    }
  }

  // ── Equipment ──
  // DDB PDFs store equipment in "Equipment" and weapons in "Wpn Name", "Wpn Name 2", etc.
  // We log equipment items as warnings since inventory is on the runtime STATE row.
  const equipmentText = fields['Equipment'] ?? '';
  if (equipmentText.trim()) {
    const items = equipmentText.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    if (items.length > 0) {
      warnings.push(`Equipment found (${items.length} items) — add to inventory manually`);
    }
  }

  // ── Weapons ──
  // DDB PDFs use "Wpn Name", "Wpn Name 2", "Wpn Name 3" for weapon names,
  // and "Wpn1 AtkBonus", "Wpn1 Damage" etc. for stats. Log for manual entry.
  const weaponNames: string[] = [];
  for (const key of ['Wpn Name', 'Wpn Name 2', 'Wpn Name 3']) {
    const name = fields[key]?.trim();
    if (name) weaponNames.push(name);
  }
  if (weaponNames.length > 0) {
    warnings.push(`Weapons found: ${weaponNames.join(', ')} — add to inventory manually`);
  }

  return {
    className: classLevel.className,
    subclass: subclass ?? undefined,
    level: classLevel.level,
    hitDieTotal,
    baseStats,
    asiDeltas: {}, // Can't distinguish base from ASI in a PDF export.
    featDeltas: {}, // Ditto.
    proficiencies,
    feats,
    spells,
    race,
    background,
    alignment,
    walkSpeed,
    languages: languages.length > 0 ? languages : undefined,
    personalityTraits,
    ideals,
    bonds,
    flaws,
    armorClass,
    spellcastingAbility,
    spellSaveDc,
    spellAttackBonus,
    warnings,
  };
}

// ────────────────────────────────────────────────────────────────────
// Internal helpers
// ────────────────────────────────────────────────────────────────────

function parseClassLevel(
  raw: string,
): { className: string; level: number } | null {
  // "Wizard 5", "Fighter 3 / Rogue 2", etc.
  const classPattern =
    /\b(Artificer|Barbarian|Bard|Blood\s*Hunter|Cleric|Druid|Fighter|Monk|Paladin|Ranger|Rogue|Sorcerer|Warlock|Wizard)\s+(\d{1,2})\b/i;
  const m = raw.match(classPattern);
  if (m) {
    return {
      className: m[1].charAt(0).toUpperCase() + m[1].slice(1).toLowerCase(),
      level: parseInt(m[2], 10),
    };
  }
  return null;
}

function parseHitDice(raw: string): { count: number; die: number } | null {
  // "5d6", "3d10", etc.
  const m = raw.match(/(\d+)d(\d+)/i);
  if (m) return { count: parseInt(m[1], 10), die: parseInt(m[2], 10) };
  return null;
}

/**
 * Known 5e languages for filtering out non-language proficiencies
 * from the combined "ProficienciesLang" field.
 */
const KNOWN_LANGUAGES = new Set([
  'common', 'dwarvish', 'elvish', 'giant', 'gnomish', 'goblin',
  'halfling', 'orc', 'abyssal', 'celestial', 'draconic', 'deep speech',
  'infernal', 'primordial', 'sylvan', 'undercommon', 'druidic',
  "thieves' cant", 'thieves cant', 'auran', 'aquan', 'ignan', 'terran',
]);

/**
 * Parse language names from the DDB PDF "ProficienciesLang" field.
 * This field contains a mix of languages, tool proficiencies, weapon
 * proficiencies, and armor proficiencies — we filter for known languages.
 */
function parseLanguages(raw: string): string[] {
  if (!raw.trim()) return [];
  const languages: string[] = [];
  // Split on commas, semicolons, or newlines.
  const parts = raw.split(/[,;\n]/).map(s => s.trim()).filter(Boolean);
  for (const part of parts) {
    const lower = part.toLowerCase();
    if (KNOWN_LANGUAGES.has(lower)) {
      // Title-case it.
      languages.push(part.charAt(0).toUpperCase() + part.slice(1));
    }
  }
  return languages;
}

/** Extract feat names from the FeaturesTraits form fields. */
function parseFeats(fields: Record<string, string>): string[] {
  const feats: string[] = [];
  // Combine all FeaturesTraits fields.
  const traitText = [
    fields['FeaturesTraits1'],
    fields['FeaturesTraits2'],
    fields['FeaturesTraits3'],
  ]
    .filter(Boolean)
    .join('\n');

  // Look for the "=== FEATS ===" section.
  const featsSection = traitText.match(
    /===\s*FEATS\s*===\s*\n([\s\S]*?)(?:===|$)/i,
  );
  if (featsSection) {
    const lines = featsSection[1].split('\n');
    for (const line of lines) {
      // Feat entries: "* Feat Name • Source"
      const m = line.match(/\*\s+(.+?)(?:\s*•\s*.+)?$/);
      if (m) {
        const name = m[1].trim();
        if (name && !name.startsWith('|') && name.length > 2) {
          feats.push(name);
        }
      }
    }
  }
  return feats;
}

/** Try to extract the subclass from the FeaturesTraits fields. */
function parseSubclass(fields: Record<string, string>): string | null {
  const traitText = [
    fields['FeaturesTraits1'],
    fields['FeaturesTraits2'],
    fields['FeaturesTraits3'],
  ]
    .filter(Boolean)
    .join('\n');

  // Common DDB patterns: "| School of Necromancy", "| Path of the Berserker", etc.
  const patterns = [
    /\|\s*(School\s+of\s+\w+)/i,
    /\|\s*(College\s+of\s+\w+)/i,
    /\|\s*(Path\s+of\s+(?:the\s+)?\w+)/i,
    /\|\s*(Circle\s+of\s+(?:the\s+)?\w+)/i,
    /\|\s*(Oath\s+of\s+(?:the\s+)?\w+)/i,
    /\|\s*(\w[\w\s]+?Domain)/i,
    /\|\s*(Way\s+of\s+(?:the\s+)?\w+)/i,
    /\|\s*(Order\s+of\s+(?:the\s+)?\w+)/i,
    /\|\s*(The\s+\w+(?:\s+Patron)?)/i,
  ];
  for (const p of patterns) {
    const m = traitText.match(p);
    if (m) return m[1].trim();
  }
  return null;
}

/**
 * Extract spells from DDB PDF form fields.
 *
 * DDB PDFs store spells across multiple form field naming patterns:
 * - "Spells 1018", "Spells 1019", etc. — individual spell name fields
 * - "Spells1", "Spells2" — textarea blocks with spell lists
 * - "SL#" + "SPName#" — numbered spell entries with level prefix
 *
 * The most reliable pattern in DDB fillable PDFs is field names matching
 * "Spells 10XX" for cantrips and "Spells 10XX" for leveled spells, with
 * the page/section determining the level. We extract names and try to
 * assign levels from the field name range.
 */
function parseSpells(fields: Record<string, string>, warnings: string[]): SpellEntry[] {
  const spells: SpellEntry[] = [];
  const seen = new Set<string>();

  // Pattern 1: Individual "Spells XXXX" form fields.
  // DDB PDFs use ranges of field numbers grouped by spell level:
  // Cantrips: 1014-1021, Level 1: 1022-1035, Level 2: 1036-1048, etc.
  const spellRanges: [number, number, number][] = [
    [1014, 1021, 0], // cantrips
    [1022, 1035, 1], // level 1
    [1036, 1048, 2], // level 2
    [1049, 1061, 3], // level 3
    [1062, 1074, 4], // level 4
    [1075, 1087, 5], // level 5
    [1088, 1098, 6], // level 6
    [1099, 1107, 7], // level 7
    [1108, 1114, 8], // level 8
    [1115, 1121, 9], // level 9
  ];

  for (const [start, end, level] of spellRanges) {
    for (let n = start; n <= end; n++) {
      const val = fields[`Spells ${n}`]?.trim();
      if (val && !seen.has(val.toLowerCase())) {
        seen.add(val.toLowerCase());
        spells.push({ name: val, level, prepared: true });
      }
    }
  }

  // Pattern 2: Generic "SpellName#" fields.
  for (const [key, val] of Object.entries(fields)) {
    const m = key.match(/^SpellName(\d+)$/);
    if (!m || !val?.trim()) continue;
    const name = val.trim();
    if (seen.has(name.toLowerCase())) continue;
    seen.add(name.toLowerCase());
    // Try to get level from a corresponding "SL#" field.
    const lvlStr = fields[`SL${m[1]}`];
    const level = lvlStr ? parseInt(lvlStr, 10) || 0 : 0;
    spells.push({ name, level, prepared: true });
  }

  // Pattern 3: "Spells1", "Spells2" etc. — block text with one spell per line.
  for (let block = 1; block <= 5; block++) {
    const text = fields[`Spells${block}`];
    if (!text) continue;
    for (const line of text.split('\n')) {
      const trimmed = line.replace(/^[\-\*•]\s*/, '').trim();
      if (!trimmed || trimmed.length < 2) continue;
      // Skip lines that look like headers or dividers.
      if (/^(cantrip|level|---)/i.test(trimmed)) continue;
      if (seen.has(trimmed.toLowerCase())) continue;
      seen.add(trimmed.toLowerCase());
      // Can't determine level from block text — default to 0, user can adjust.
      spells.push({ name: trimmed, level: 0, prepared: true });
      warnings.push(`Spell "${trimmed}" imported without level — please verify`);
    }
  }

  return spells;
}

const CLASS_HIT_DICE: Record<string, number> = {
  barbarian: 12,
  fighter: 10,
  paladin: 10,
  ranger: 10,
  bard: 8,
  cleric: 8,
  druid: 8,
  monk: 8,
  rogue: 8,
  warlock: 8,
  'blood hunter': 10,
  artificer: 8,
  sorcerer: 6,
  wizard: 6,
};
